import { AdaptedGoalValues } from './adapted_goal_levels'
export {
  levelToAttributeNames,
  gradeGoalLabel,
  adaptedGoalsArrayFromSavedGoal,
  adaptedGoalOrObjectiveLevelLabelMapping,
  isSubjectELL,
  isSubjectLifeSkills,
  isCriteriaForGrade,
  isCriteriaForSubjects,
  shouldShowGoalReview
}

// these levels map to attribute names that are part of the saved goal document
const levelToAttributeNames = {
  [AdaptedGoalValues.MILD]: 'mild_goal',
  [AdaptedGoalValues.MODERATE]: 'moderate_goal',
  [AdaptedGoalValues.INTENSE]: 'intense_goal',
  grade: 'grade_level_goal',

  // alt assessment levels
  [AdaptedGoalValues.ATTENTION]: 'attention_goal',
  [AdaptedGoalValues.VISUAL]: 'visual_goal',
  [AdaptedGoalValues.HEARING]: 'hearing_goal',
  [AdaptedGoalValues.PHYSICAL]: 'physical_goal',
  [AdaptedGoalValues.EXPRESSIVE_LANGUAGE]: 'expressive_language_goal'
}

// generate the inverse mapping of levelToAttributeNames
let reverseLevelToAttributeNames = Object.keys(levelToAttributeNames).reduce((acc, level) => {
  acc[levelToAttributeNames[level]] = level
  return acc
}, {})

function isSubjectELL(subject) {
  return subject === 'English Learners'
}

function isSubjectLifeSkills(subject) {
  return (
    subject === 'Alt Academic & Life Skills' ||
    subject === 'Life Skills' ||
    subject === 'Alt Academics'
  )
}

function gradeGoalLabel(subject) {
  if (isSubjectELL(subject)) {
    return 'Advanced Level'
  }
  if (isSubjectLifeSkills(subject)) {
    return 'Core Goal'
  }
  return 'Grade Level'
}

function adaptedGoalsArrayFromSavedGoal(savedGoal) {
  let possibleAttributes = [
    'mild_goal',
    'moderate_goal',
    'intense_goal',
    'attention_goal',
    'visual_goal',
    'hearing_goal',
    'physical_goal',
    'expressive_language_goal'
  ]
  let adaptedGoals = possibleAttributes.filter(attr => savedGoal[attr]).map(attr => ({
    level: reverseLevelToAttributeNames[attr],
    text: savedGoal[attr]
  }))

  return adaptedGoals
}

const anchorObjectiveLabels = {
  [AdaptedGoalValues.FIRST]: '1st Quarter',
  [AdaptedGoalValues.SECOND]: '2nd Quarter',
  [AdaptedGoalValues.THIRD]: '3rd Quarter'
}
const defaultLevelLabels = {
  [AdaptedGoalValues.MILD]: 'Mild Level of Support',
  [AdaptedGoalValues.MODERATE]: 'Moderate Level of Support',
  [AdaptedGoalValues.INTENSE]: 'Intense Level of Support',
  ...anchorObjectiveLabels
}

const ellLevelLabels = {
  [AdaptedGoalValues.INTERMEDIATE]: 'Intermediate Level',
  [AdaptedGoalValues.BEGINNER]: 'Beginner Level',
  ...anchorObjectiveLabels
}

const lifeSkillsLevelLabels = {
  [AdaptedGoalValues.ATTENTION]: 'Attention Support',
  [AdaptedGoalValues.VISUAL]: 'Visual Support',
  [AdaptedGoalValues.HEARING]: 'Hearing Support',
  [AdaptedGoalValues.PHYSICAL]: 'Physical Support',
  [AdaptedGoalValues.EXPRESSIVE_LANGUAGE]: 'Expressive Language Support',
  ...anchorObjectiveLabels
}
function adaptedGoalOrObjectiveLevelLabelMapping(subject) {
  if (isSubjectELL(subject)) {
    return ellLevelLabels
  }
  if (isSubjectLifeSkills(subject)) {
    return lifeSkillsLevelLabels
  }
  return defaultLevelLabels
}


function isCriteriaForGrade(criteria, grades) {
  if (!criteria?.grades || criteria.grades.length === 0) {
    return true // no grades means avail for all
  }
  return criteria.grades.some(grade => grades.includes(grade))
}

function isCriteriaForSubjects(criteria, subjects) {
  if (!criteria?.subjects || criteria.subjects.length === 0) {
    return true // no subjects means avail for all
  }
  return criteria.subjects.some(subject => subjects.includes(subject))
}

/**
 * Determines if goal review functionality should be shown
 * @param {Object} params - Parameters for determining visibility
 * @param {string|null} params.contentReviewCriteriaSetName - The criteria set name (empty string for default)
 * @param {boolean} params.goalCustomized - Whether the goal has been customized
 * @param {boolean} params.aiFeatureVisibility - From visibility store
 * @param {boolean} params.goalReviewVisibility - From visibility store
 * @param {string|null} params.goalUuid - The goal UUID
 * @param {Object|null} params.contentReviewCriteria - The review criteria
 * @returns {boolean} Whether to show goal review functionality
 */
function shouldShowGoalReview({
  contentReviewCriteriaSetName,
  goalCustomized,
  aiFeatureVisibility,
  goalReviewVisibility,
  goalUuid,
  contentReviewCriteria
}) {

  const customizationCondition = Boolean((!goalCustomized) ||
    (contentReviewCriteriaSetName && contentReviewCriteriaSetName !== '' && contentReviewCriteriaSetName !== null && goalCustomized))

  const otherConditions = Boolean(
    aiFeatureVisibility &&
    goalReviewVisibility &&
    goalUuid &&
    contentReviewCriteria
  )

  const result = Boolean(customizationCondition && otherConditions)
  return result
}